import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { HexColorPicker, HexColorInput } from "react-colorful";
import classNames from "classnames";

export default function ColorPicker({
    color,
    onChange,
    className,
    alignment = "right",
    disabled
}: {
    color: string | undefined;
    onChange: ((color: string) => void) | undefined;
    className?: string;
    alignment?: "right" | "left";
    disabled?: boolean;
}) {
    const isDisabled = disabled ?? !onChange;
    const popover = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({ opacity: 0 });

    const [newColor, setNewColor] = useState(color);

    function close() {
        setIsOpen(false);
    }

    useClickOutside(popover, close, isOpen && !isDisabled, triggerRef);

    useEffect(() => {
        setNewColor(color);
    }, [color]);

    // Calculate and update popover position
    useEffect(() => {
        if (isOpen && triggerRef.current) {
            const updatePosition = () => {
                const rect = triggerRef.current.getBoundingClientRect();
                const popoverWidth = 224;
                const popoverHeight = 260;

                let top = rect.top + rect.height / 2 - popoverHeight / 2;
                let left = alignment === "left" ? rect.left - popoverWidth - 8 : rect.right + 8;

                // Keep within viewport
                if (top < 8) top = 8;
                if (top + popoverHeight > window.innerHeight - 8) {
                    top = window.innerHeight - popoverHeight - 8;
                }
                if (left < 8) left = 8;
                if (left + popoverWidth > window.innerWidth - 8) {
                    left = window.innerWidth - popoverWidth - 8;
                }

                setPopoverStyle({ position: "fixed", top, left, zIndex: 100, opacity: 1 });
            };

            updatePosition();
            window.addEventListener("scroll", updatePosition, true);
            window.addEventListener("resize", updatePosition);

            return () => {
                window.removeEventListener("scroll", updatePosition, true);
                window.removeEventListener("resize", updatePosition);
            };
        }
    }, [isOpen, alignment]);

    // Debounced onChange
    useEffect(() => {
        if (isOpen && !isDisabled && onChange) {
            const timeoutId = setTimeout(() => {
                onChange(newColor);
            }, 50);
            return () => clearTimeout(timeoutId);
        }
    }, [newColor, isOpen, onChange, isDisabled]);

    const popoverContent = isOpen && !isDisabled && (
        <div
            className={classNames(
                "flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-2 shadow-lg",
                className
            )}
            ref={popover}
            style={popoverStyle}
        >
            <HexColorPicker color={newColor} onChange={setNewColor} />
            <HexColorInput
                color={newColor}
                onChange={setNewColor}
                placeholder="Type a color"
                prefixed
                alpha
                className="input2"
            />
        </div>
    );

    return (
        <div className="relative">
            <div
                ref={triggerRef}
                className={classNames(
                    isOpen && !isDisabled
                        ? "shadow-lg ring-black ring-opacity-30"
                        : "ring-black ring-opacity-30",
                    !isDisabled && "cursor-pointer hover:shadow-lg hover:ring-opacity-70",
                    "color h-3 w-3 rounded-full border-1 border-white ring-1 transition duration-200"
                )}
                style={{ backgroundColor: newColor }}
                onClick={(e) => {
                    if (isDisabled) return;
                    e.stopPropagation();
                    setIsOpen((prev) => !prev);
                }}
            />
            {popoverContent && ReactDOM.createPortal(popoverContent, document.body)}
        </div>
    );
}

const useClickOutside = (ref, handler, isActive = true, triggerRef = null) => {
    useEffect(() => {
        if (!isActive) return;

        const handleClickOutside = (event) => {
            // Don't close if clicking on the trigger or inside the popover
            if (triggerRef && triggerRef.current && triggerRef.current.contains(event.target)) {
                return;
            }
            if (ref.current && !ref.current.contains(event.target)) {
                handler(event);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref, handler, isActive, triggerRef?.current]);
};
